import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const FAQS = [
  {
    q: 'What is Little Blue Numbers?',
    a: "Little Blue Numbers is a numerology and astrology app for families that turns a name and date of birth into a personalised blueprint reading — explaining who that person really is, what they need, and how to love them well at exactly the chapter of life they're in. Use it for your children, yourself, your partner, your parents. Everyone is born with a blueprint — and once you can read it, everything makes more sense.",
  },
  {
    q: 'How is numerology different from astrology?',
    a: "They're two traditions that have always belonged together. Astrology reads the sky at the moment you arrived — the position of the planets, your star sign, the cosmic weather you were born under. Numerology reads the patterns hidden in your full name and date of birth — your soul's mathematics, the blueprint that doesn't change. We weave your star sign through every reading because together the two stories tell you something neither can alone — who you are at your deepest level, and the cosmic context you're living it inside.",
  },
  {
    q: 'What is a Life Path number?',
    a: "Your Life Path is the most significant number in your blueprint. It's calculated from your full date of birth and reveals your soul's main journey — the core of who you are, what you came here to learn, and the life you're here to live. A Life Path 7 is an old soul and seeker; a Life Path 3 is a natural communicator and creator; a Life Path 33 is a master teacher. Every number has its own beautiful story.",
  },
  {
    q: 'What are master numbers like 11, 22, 33, 44?',
    a: "Master numbers are the double-digit repeating numbers (11, 22, 33, 44, 55, 66, 77, 88, 99) that appear in your blueprint and are not reduced further because they carry extra spiritual significance. If your calculations land on a master number, it's a sign of heightened potential — and often a heightened life challenge that matches it. We display master numbers as '33/6' or '44/8' so you can see both the master and the root.",
  },
  {
    q: 'How do I use the readings for my child?',
    a: "Parents use Little Blue Numbers to understand what's going on beneath the surface with their children — especially when a child's behaviour feels confusing. Our readings are written directly to the parent, explaining who the child is, what they need to feel safe and seen, and how their numbers show up in play, learning, and friendships. Read it once, then return to it whenever your child is in a new phase.",
  },
  {
    q: 'Can I get a reading for myself?',
    a: "Yes. Switch to 'For myself' on the reading tool. Adult readings speak directly to you about your Life Path, your Soul Urge, your Personal Year, and what this chapter of your life is asking of you. We offer life-stage-tailored readings from Young Adult all the way through to Elder — the wisdom each chapter needs is different.",
  },
  {
    q: 'Do I need an account?',
    a: "No — you can generate one reading without an account, instantly. To unlock more readings, save them, and come back to them any time, we ask for just your name and email so we can build your blueprint history and family profile. We never share your data.",
  },
  {
    q: 'How much does it cost?',
    a: "Right now, every reading on Little Blue Numbers is open to anyone — we're in our validation phase, testing what families actually find valuable before launching subscription features like deeper readings, compatibility analysis, and annual Personal Year emails. Anyone who joins now becomes a founding member and locks in a discounted price for life when subscriptions open.",
  },
  {
    q: 'What does "founding member" mean?',
    a: "Founding members are the families who stand with us before subscriptions launch. In return, you get a permanent founding-member discount when paid features open, first access to the full Phase 2 reading, full family profiles and compatibility readings, an annual Personal Year letter on your birthday, and a direct line to me to shape what we build next. It's our way of saying thank you for believing early.",
  },
  {
    q: 'Who is behind Little Blue Numbers?',
    a: "Jemma Di Lorito founded Little Blue Numbers after years of reading numerology for friends and family. She's a Life Path 33/6 — the master teacher — and built this app to put the same insight she gives privately into the hands of every family.",
  },
  {
    q: 'Can I share a reading with my partner or family?',
    a: "Yes. After any reading you can click 'Share' to get a public link that shows the opening of the reading (with full numbers and life stage) to anyone you send it to. They'll see a preview and be invited to create their own blueprint. It's a beautiful way to help a partner, grandparent, or friend understand your child too.",
  },
  {
    q: "What if I don't know my full birth name?",
    a: "Use the name on your birth certificate if you can find it — that's the one numerology uses as your core blueprint. If you only know your current name, that's fine too — we'll still generate a reading, and you can always come back and refine it later. Married/chosen names create a second layer that can be read on top of your birth blueprint.",
  },
  {
    q: 'How accurate is this?',
    a: "It's a tradition, not a science — but families consistently tell us our readings feel uncannily accurate. That's because the AI is guided by a deep, carefully crafted understanding of the patterns hidden in your name and date of birth, woven together with where you are in life right now. We weave your star sign in too — together they add a whole new layer of insight. The proof is in reading it. Try one for someone you know well.",
  },
];

export default function FAQPage() {
  const navigate = useNavigate();
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '56px 24px 96px' }} data-testid="faq-page">
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <div style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 10 }}>Help & Info</div>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 300, color: 'var(--deep)', marginBottom: 14, lineHeight: 1.1 }}>
          Frequently asked <em>questions</em>
        </h1>
        <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.7, maxWidth: 500, margin: '0 auto' }}>
          Everything you might be wondering about numerology readings, your family's blueprint, and how Little Blue Numbers works.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} itemScope itemType="https://schema.org/FAQPage">
        {FAQS.map((item, i) => {
          const isOpen = openIdx === i;
          return (
            <div
              key={i}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              data-testid={`faq-item-${i}`}
              style={{
                background: 'white', border: '1px solid var(--border)', borderRadius: 14,
                overflow: 'hidden', transition: 'all 0.2s'
              }}
            >
              <button
                onClick={() => setOpenIdx(isOpen ? -1 : i)}
                data-testid={`faq-toggle-${i}`}
                style={{
                  width: '100%', background: 'none', border: 'none', textAlign: 'left',
                  padding: '22px 24px', cursor: 'pointer', display: 'flex',
                  justifyContent: 'space-between', alignItems: 'center', gap: 18,
                  fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 400,
                  color: 'var(--deep)', lineHeight: 1.3,
                }}
              >
                <span itemProp="name">{item.q}</span>
                <span style={{ fontSize: 22, color: 'var(--muted)', transform: isOpen ? 'rotate(45deg)' : 'rotate(0)', transition: 'transform 0.25s', flexShrink: 0 }}>+</span>
              </button>
              {isOpen && (
                <div
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                  style={{ padding: '0 24px 24px', borderTop: '1px solid var(--border)' }}
                >
                  <div itemProp="text" style={{ fontSize: 15, lineHeight: 1.8, color: 'var(--body, #3a3a4d)', paddingTop: 18 }}>
                    {item.a}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ textAlign: 'center', marginTop: 56 }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 300, marginBottom: 14 }}>Still curious?</div>
        <button className="tool-btn" onClick={() => navigate('/tool')} data-testid="faq-cta-tool">Get your reading →</button>
      </div>
    </div>
  );
}
